var _dec, _dec2, _class, _CourseUnitInternal;
import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { AsyncPriceCourseCard, BrowseCourseBadgesContext, BrowseCourseCard, BrowseCourseCardContext, COURSE_IN_SUBS_PLAN_BADGE_FAMILIES, withBrowseCourseBadgesContext } from '@udemy/browse-course';
import { discoveryTracker } from '@udemy/browse-event-tracking';
import { TrackingContextProvider } from '@udemy/event-tracking';
import { withMatchMedia } from '@udemy/hooks';
import { withI18n } from '@udemy/i18n';
import { withCourseCardUBGuard } from '@udemy/react-card-components';
import { CourseCardSkeleton } from '@udemy/react-reveal-components';
import { CarouselSlide, CarouselWithFocusOrder } from '@udemy/react-structure-components';
import { BorderedCourseCard } from '@udemy/shared-one-udemy-components';
import { noop } from '@udemy/shared-utils';
import { withUDData } from '@udemy/ud-data';
import { AddToCart } from '../add-to-cart/add-to-cart.react-component';
import { CourseDetailsQuickViewBoxLazy } from '../browse-course/course-details/course-details-quick-view-box-lazy.react-component';
import { InfiniteScrollContainer } from '../infinite-scroll-container/infinite-scroll-container.react-component';
import { StackedCards } from '../stacked-cards/stacked-cards.react-component';
import { UnitTitle } from '../unit-title/unit-title.react-component';
import { SingleColumnCourseCard } from './components/single-column-course-card.react-component';
import stylesExperimental from "./course-unit-experimental.module.css";
import { CourseUnitSkeleton } from './course-unit-skeleton.react-component';
import { CourseUnitStore } from './course-unit.mobx-store';
import styles from "./course-unit.module.css";

/**
 * Experimental Course Card
 *
 * @internal
 *
 * @privateRemarks
 *
 * Exported for reference in specs. Otherwise not for consumption outside of this module.
 */
export const ExperimentalBorderedCourseCard = withCourseCardUBGuard(BorderedCourseCard);
export let CourseUnitInternal = (_dec = withMatchMedia({
  hasHover: '(hover: hover)',
  hasCoarsePointer: '(any-pointer: coarse)'
}), _dec2 = inject('discoveryUnitsStore', 'funnelLogContextStore'), _dec(_class = _dec2(_class = observer(_class = (_CourseUnitInternal = class CourseUnitInternal extends React.Component {
  constructor(_props) {
    var _this$store$unit$titl;
    super(_props);
    this.id = void 0;
    this.store = void 0;
    this.fetchUnit = async () => {
      return await this.store.fetchUnit({
        skipPrice: true
      });
    };
    this.renderCourseImage = (CourseImageComponent, props) => {
      return /*#__PURE__*/React.createElement(CourseImageComponent, Object.assign({}, props, {
        onLoad: this.props.onLoad
      }));
    };
    this.getWrappedBrowseCourseBadgeHandlerOptions = () => {
      const options = this.props.browseBadgeHandlerOptions;
      if (this.props.layout === 'multirow') {
        return {
          ...(options !== null && options !== void 0 ? options : {}),
          numberOfBadgesToShow: 1,
          filter: (badge, course) => {
            var _ref, _options$filter;
            return (
              /**
               * Do not show the subscription badges in multirow layout
               * https://udemyjira.atlassian.net/browse/CVR-42
               */
              (_ref = ![...COURSE_IN_SUBS_PLAN_BADGE_FAMILIES].includes(badge) && (options === null || options === void 0 || (_options$filter = options.filter) === null || _options$filter === void 0 ? void 0 : _options$filter.call(options, badge, course))) !== null && _ref !== void 0 ? _ref : true
            );
          }
        };
      }
      return options;
    };
    const {
      discoveryUnitsStore,
      funnelLogContextStore,
      unit,
      udData
    } = _props;
    const {
      pageType
    } = discoveryUnitsStore;
    this.store = new CourseUnitStore(pageType, unit, udData);
    // only use alphanumeric characters for html id
    this.id = `course-unit-container-${(_this$store$unit$titl = this.store.unit.title) === null || _this$store$unit$titl === void 0 ? void 0 : _this$store$unit$titl.replace(/[^a-z0-9]/gi, '')}`;
    funnelLogContextStore.updateContext({
      context2: 'featured',
      subcontext: unit.title,
      subcontext2: unit === null || unit === void 0 ? void 0 : unit.id
    });
  }
  componentDidMount() {
    if (!this.store.unit.items.length) {
      this.fetchUnit();
    }
  }

  /**
   * TODO: Once we figure out what we want to do for subtitles, use this and the get subtitleTemplate
   * property to pass subtitle to UnitTitle component
   */
  get subtitleTemplates() {
    const {
      gettext
    } = this.props;
    return [gettext('Explore our %(title)s courses from our top-rated instructors'), gettext('Browse our collection of %(title)s courses from our top-rated instructors')];
  }
  get subtitleTemplate() {
    const bestsellerUnits = this.props.discoveryUnitsStore.units.filter(unit => unit.type === 'bestseller' && unit.raw_title === 'Top {} courses in {}');
    const thisIndex = bestsellerUnits.findIndex(unit => unit.tracking_id === this.props.unit.tracking_id);
    return thisIndex > -1 && thisIndex < this.subtitleTemplates.length ? this.subtitleTemplates[thisIndex] : null;
  }
  render() {
    if (!this.store.unit.items.length && this.store.loading) {
      var _this$props$loadingSk;
      return (_this$props$loadingSk = this.props.loadingSkeleton) !== null && _this$props$loadingSk !== void 0 ? _this$props$loadingSk : /*#__PURE__*/React.createElement(CourseUnitSkeleton, {
        layout: this.props.layout
      });
    }
    const courseCardSize = this.props.layout === 'multirow' ? 'small' : 'medium';
    const showQuickViewBox = this.props.hasHover && this.props.showQuickViewBox;
    const unitItems = this.store.unit.items;

    // Only pass addToCartContext to components that actually need it
    // Don't spread it to courseCardProps to avoid DOM warnings
    const courseCardProps = this.props.courseCardProps || {};
    const renderCta = course => {
      return /*#__PURE__*/React.createElement(AddToCart, {
        allowAddToCartSuccessModal: this.props.allowAddToCartSuccessModal,
        cartButtonTextGoToCart: this.props.cartButtonTextGoToCart,
        cartButtonDisableOnGoToCart: this.props.cartButtonDisableOnGoToCart,
        disableMobileRedirect: this.props.disableMobileAddToCartRedirect,
        buyables: [course],
        size: this.props.addToCartButtonSize,
        udStyle: this.props.addToCartButtonStyle,
        addToCartContext: this.props.addToCartContext
      });
    };
    const courseCards = unitItems.map((course, i) => {
      const titleId = `course-title-${this.id}-${course.id}`;
      return /*#__PURE__*/React.createElement(TrackingContextProvider, {
        key: course.id,
        trackingContext: {
          trackImpressionFunc: discoveryTracker.trackDiscoveryImpression,
          index: i,
          backendSource: this.store.backendSource,
          uiRegion: this.props.uiRegion,
          relatedSourceType: this.props.relatedSourceType,
          relatedSourceId: this.props.relatedSourceId
        }
      }, /*#__PURE__*/React.createElement(CourseDetailsQuickViewBoxLazy, {
        allowAddToCartSuccessModal: this.props.allowAddToCartSuccessModal,
        cartButtonTextGoToCart: this.props.cartButtonTextGoToCart,
        cartButtonDisableOnGoToCart: this.props.cartButtonDisableOnGoToCart,
        course: course,
        showQuickViewBox: showQuickViewBox,
        showCta: this.props.showAddToCartInQuickview,
        addToCartContext: this.props.addToCartContext,
        courseCard: this.props.layout === 'singlecol' ? /*#__PURE__*/React.createElement(SingleColumnCourseCard, {
          cta: this.props.showAddToCartInCourseCard ? renderCta(course) : undefined,
          course: course,
          courseCardProps: courseCardProps,
          showInstructorDetails: this.props.showInstructorDetails
        }) : /*#__PURE__*/React.createElement(AsyncPriceCourseCard, Object.assign({
          course: course,
          size: courseCardSize,
          width: this.props.layout === 'multirow' ? 'fixed' : 'flexible'
        }, i === 0 && {
          renderCourseImage: this.renderCourseImage
        }, {
          showDetails: false,
          className: styles['course-card'],
          cta: this.props.showAddToCartInCourseCard ? renderCta(course) : undefined,
          titleId: titleId
        }, courseCardProps))
      }));
    });
    const renderSingleColumnCourseCard = () => {
      return this.props.disableInfiniteScroll ? /*#__PURE__*/React.createElement("div", {
        className: styles['single-col-container']
      }, /*#__PURE__*/React.createElement(StackedCards, {
        maxCardsPerStack: this.props.maxCardsPerStack,
        maxCards: this.props.maxCards,
        cards: courseCards.map((card, i) => /*#__PURE__*/React.createElement("div", {
          key: i
        }, card)),
        hasMore: this.store.hasMore,
        onMore: async () => {
          await this.fetchUnit();
        },
        showDividers: this.props.showDividers,
        smallGap: !this.props.showDividers
      })) : /*#__PURE__*/React.createElement("div", {
        className: classNames(styles['single-col-container'], {
          [styles['single-col-container-with-dividers']]: this.props.showDividers
        }),
        "data-testid": "course-unit-infinitescroll"
      }, /*#__PURE__*/React.createElement(InfiniteScrollContainer, {
        onLastChildEnter: async () => {
          await this.fetchUnit();
        }
      }, courseCards.map((card, i) => /*#__PURE__*/React.createElement(CourseCardWrapper, {
        key: i
      }, card))));
    };
    if (this.store.hasMore && !this.props.showPager) {
      const numCards = this.props.layout === 'multirow' ? 3 : 1;
      for (let i = 0; i < numCards; i++) {
        courseCards.push(/*#__PURE__*/React.createElement(CourseCardSkeleton, {
          size: this.props.layout === 'singlerow' ? 'medium' : 'small',
          key: courseCards.length + i
        }));
      }
    }
    const cardComponent = this.props.renderExperimentalCourseCards ? ExperimentalBorderedCourseCard : BrowseCourseCard.defaultCardComponent;

    /**
     * Wrap each course card (or skeleton)
     * in a CarouselSlide with proper accessibility attributes.
     */
    const slides = courseCards.map((card, i) => {
      // Select matching unit item if available, otherwise fall back to undefined (skeleton)
      const course = i < unitItems.length ? unitItems[i] : undefined;

      // Render wrapped CourseCardSkeleton in undefined case
      if (!course) {
        return /*#__PURE__*/React.createElement(CarouselSlide, {
          key: `skeleton-${i}`,
          "data-index": i
        }, card);
      }
      const titleId = `course-title-${this.id}-${course.id}`;
      return /*#__PURE__*/React.createElement(CarouselSlide, {
        key: course.id,
        "data-index": i,
        "aria-labelledby": titleId
      }, card);
    });
    const carouselClassName = classNames({
      [styles['multi-row-container']]: this.props.layout === 'multirow',
      [styles.grid]: this.props.layout !== 'multirow' && !this.props.renderExperimentalCourseCards,
      [styles[`col-${this.props.gridCols}`]]: !!this.props.gridCols,
      [stylesExperimental.grid]: this.props.renderExperimentalCourseCards
    });
    return /*#__PURE__*/React.createElement("div", {
      className: this.props.className
    }, this.props.showTitle && /*#__PURE__*/React.createElement(UnitTitle, {
      unit: {
        ...this.props.unit,
        actionLink: this.props.actionLink
      },
      typography: this.props.titleTypography,
      className: classNames(styles['title-wrapper'])
    }), this.props.subtitle, /*#__PURE__*/React.createElement(BrowseCourseBadgesContext.Provider, {
      value: {
        browseBadgeHandlerOptions: this.getWrappedBrowseCourseBadgeHandlerOptions()
      }
    }, /*#__PURE__*/React.createElement(BrowseCourseCardContext.Provider, {
      value: {
        cardComponent
      }
    }, this.props.layout === 'singlecol' ? renderSingleColumnCourseCard() : /*#__PURE__*/React.createElement(CarouselWithFocusOrder, Object.assign({
      className: carouselClassName,
      "data-testid": "course-unit-carousel",
      id: this.id,
      onLoadMore: async () => {
        await this.fetchUnit();
      },
      showPager: this.props.showPager,
      slides: slides,
      allowScroll: !!this.props.hasCoarsePointer,
      pagerButtonClassName: this.props.renderExperimentalCourseCards ? undefined : styles['pager-button']
    }, this.props.carouselProps)))));
  }
}, _CourseUnitInternal.defaultProps = {
  allowAddToCartSuccessModal: true,
  disableMobileAddToCartRedirect: false,
  className: undefined,
  cartButtonTextGoToCart: undefined,
  cartButtonDisableOnGoToCart: undefined,
  layout: 'multirow',
  disableInfiniteScroll: false,
  maxCards: 12,
  maxCardsPerStack: 3,
  showTitle: false,
  showInstructorDetails: false,
  titleTypography: 'ud-heading-xl',
  showPager: false,
  hasHover: null,
  hasCoarsePointer: null,
  fullWidth: true,
  courseCardProps: {},
  showQuickViewBox: true,
  renderExperimentalCourseCards: false,
  onLoad: noop,
  gridCols: undefined,
  actionLink: undefined,
  subtitle: undefined,
  showAddToCartInCourseCard: false,
  addToCartButtonStyle: 'secondary',
  addToCartButtonSize: 'small',
  showAddToCartInQuickview: true,
  uiRegion: undefined,
  relatedSourceId: undefined,
  relatedSourceType: undefined,
  showDividers: true,
  addToCartContext: undefined
}, _CourseUnitInternal)) || _class) || _class) || _class);

// Wrap the course cards in a class-based component.
// This is necessary if using InfiniteScrollContainer, as the react-intersection-observer
// it depends on looks for a ref.
class CourseCardWrapper extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(React.Fragment, null, this.props.children);
  }
}
const CourseUnitWithProviders = withUDData(withI18n(withBrowseCourseBadgesContext(CourseUnitInternal)));
export { CourseUnitWithProviders as CourseUnit };
//# sourceMappingURL=course-unit.react-component.js.map